import { useMemo, useRef, useState } from 'react'
import { Play, Download, FileDown } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FormField } from '@/components/FormField'
import { Toolbar } from '@/components/Toolbar'
import { DataTable } from '@/components/DataTable'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { deriveColumns, formatCell, type ReportResultTable } from '@/components/reportRunnerUtils'

export type { ReportResultTable }

export type ReportParamDef = {
  key: string
  label: string
  type: 'text' | 'number' | 'date' | 'select'
  options?: { value: string; label: string }[]
  required?: boolean
  defaultValue?: string
  placeholder?: string
}

export type ReportDef = {
  slug: string
  name: string
  description: string
  permission?: string
  params?: ReportParamDef[]
  run: (params: Record<string, string>) => Promise<ReportResultTable[]>
}

function ResultTable({ table }: { table: ReportResultTable }) {
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      deriveColumns(table).map((c) => ({
        accessorKey: c.key,
        header: c.header,
        cell: ({ row }) => formatCell(c.key, row.original[c.key]),
      })),
    [table],
  )
  return (
    <div className="space-y-2">
      {table.title ? <h3 className="text-sm font-semibold">{table.title}</h3> : null}
      <DataTable
        columns={columns}
        rows={table.rows}
        empty={<EmptyState title="No rows" description="This report returned no data for the selected parameters." />}
      />
    </div>
  )
}

export function ReportRunner({
  title,
  description,
  reports,
}: {
  title: string
  description: string
  reports: ReportDef[]
}) {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const resultsRef = useRef<HTMLDivElement>(null)

  const visible = reports.filter((r) => !r.permission || hasPermission(r.permission as never))
  const [slug, setSlug] = useState(visible[0]?.slug ?? '')
  const selected = visible.find((r) => r.slug === slug)

  const initialParams = (def?: ReportDef): Record<string, string> => {
    const out: Record<string, string> = {}
    for (const p of def?.params ?? []) out[p.key] = p.defaultValue ?? (p.type === 'select' ? p.options?.[0]?.value ?? '' : '')
    return out
  }
  const [params, setParams] = useState<Record<string, string>>(initialParams(selected))
  const [tables, setTables] = useState<ReportResultTable[] | null>(null)
  const [running, setRunning] = useState(false)

  function pickReport(next: string) {
    setSlug(next)
    setTables(null)
    setParams(initialParams(visible.find((r) => r.slug === next)))
  }

  async function run() {
    if (!selected) return
    for (const p of selected.params ?? []) {
      if (p.required && !params[p.key]) {
        toast({ title: `${p.label} is required`, variant: 'destructive' })
        return
      }
    }
    setRunning(true)
    try {
      const result = await selected.run(params)
      setTables(result)
    } catch (err) {
      toast({ title: 'Could not run report', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
      setTables(null)
    } finally {
      setRunning(false)
    }
  }

  const hasRows = (tables ?? []).some((t) => t.rows.length > 0)

  return (
    <Page title={title} description={description}>
      <Card>
        <CardContent className="space-y-4 p-5">
          <FormField
            label="Report"
            control={
              <select
                value={slug}
                onChange={(e) => pickReport(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm sm:w-80"
              >
                {visible.map((r) => (
                  <option key={r.slug} value={r.slug}>{r.name}</option>
                ))}
              </select>
            }
          />
          {selected ? <p className="text-sm text-muted-foreground">{selected.description}</p> : null}

          {(selected?.params?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {selected!.params!.map((p) =>
                p.type === 'select' ? (
                  <FormField
                    key={p.key}
                    label={p.label}
                    control={
                      <select
                        value={params[p.key] ?? ''}
                        onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        {(p.options ?? []).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    }
                  />
                ) : (
                  <FormField
                    key={p.key}
                    label={p.required ? `${p.label} *` : p.label}
                    type={p.type === 'number' ? 'text' : p.type}
                    inputMode={p.type === 'number' ? 'numeric' : undefined}
                    placeholder={p.placeholder}
                    value={params[p.key] ?? ''}
                    onChange={(e) =>
                      setParams({
                        ...params,
                        [p.key]: p.type === 'number' ? e.target.value.replace(/\D/g, '') : e.target.value,
                      })
                    }
                  />
                ),
              )}
            </div>
          ) : null}

          <Toolbar align="start">
            <Button onClick={run} disabled={running || !selected}>
              <Play className="mr-2 size-4" /> {running ? 'Running…' : 'Run report'}
            </Button>
            {hasRows ? (
              <>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const { downloadCsvFromRoot } = await import('@/features/reporting/export')
                    downloadCsvFromRoot(resultsRef.current, `${slug}.csv`, undefined, selected?.name ?? 'Report')
                  }}
                >
                  <Download className="mr-2 size-4" /> CSV
                </Button>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    const { downloadPdfFromRoot } = await import('@/features/reporting/export')
                    downloadPdfFromRoot(resultsRef.current, selected?.name ?? 'Report')
                  }}
                >
                  <FileDown className="mr-2 size-4" /> PDF
                </Button>
              </>
            ) : null}
          </Toolbar>
        </CardContent>
      </Card>

      {running ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : tables === null ? (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="Run a report"
              description="Choose a report and parameters above, then run it to see results here. Export to CSV or PDF once it loads."
            />
          </CardContent>
        </Card>
      ) : (
        <div ref={resultsRef} className="space-y-6">
          {tables.map((t, i) => (
            <Card key={t.title ?? i}>
              <CardContent className="p-5"><ResultTable table={t} /></CardContent>
            </Card>
          ))}
          {!hasRows ? (
            <Card>
              <CardContent className="p-6">
                <EmptyState title="No data" description="This report returned no rows for the selected parameters." />
              </CardContent>
            </Card>
          ) : null}
        </div>
      )}

      <Badge variant="outline" className="mt-1">Reads live from posted data - no shadow tables</Badge>
    </Page>
  )
}
